import { useEffect, memo, useState } from 'react';
import { useRecoilState, lockInfoPopupStatusState } from '../../../../../store';
import { GameLock, LOCK_SOLUTION_SEPARATOR, LockType } from '@/_models/Game';
import { gameLockData } from '@/components/GameTour';

import { CollectBlock, MainWrap, PopupItem, SequenceBlock, TempBlock } from './style';
import { ELockInfo, ILockInfoPopup, TLockInfoPopup } from './types';

const HIDE_MS = 2000;

const LockInfoPopup = (props: ILockInfoPopup) => {
  const {
    manualData,
    setManualData,
    setShowNewHintModal,
    translation = {},
    rtl,
    answerIndicatorOn = true,
  } = props;
  const [activePopups, setActivePopups] = useState<TLockInfoPopup[]>([]);
  const [lockInfoPopupStatus, setLockInfoPopupStatus] = useRecoilState(lockInfoPopupStatusState);
  const [sequenceTimer, setSequenceTimer] = useState({ value: 0, running: false });

  useEffect(() => {
    if (
      (lockInfoPopupStatus || manualData) &&
      setLockInfoPopupStatus &&
      setActivePopups &&
      setManualData
    ) {
      //receiving ok should erase collect/sequence
      const src = lockInfoPopupStatus || manualData;
      const setter = lockInfoPopupStatus ? setLockInfoPopupStatus : setManualData;
      const currentItem: TLockInfoPopup = { id: Date.now(), ...src, timeoutId: 0 };
      if (src.value === ELockInfo.reset_sequence) {
        setActivePopups(activePopups.filter((p) => p?.obj?.id !== currentItem?.obj?.id));
        setSequenceTimer({ running: false, value: 0 });
      }
      // if (answerIndicatorOn === false && (src.value === ELockInfo.ok || src.value === ELockInfo.err)) return;
      if (
        src.value !== ELockInfo.info_collect &&
        src.value !== ELockInfo.info_sequence &&
        answerIndicatorOn
      ) {
        currentItem.timeoutId = setTimeout(close(currentItem.id), HIDE_MS);
      }
      if (src.value === ELockInfo.info_sequence && src.time) {
        const timerObj = { value: src.time, running: true };
        if (!sequenceTimer.running) {
          setTimeout(timerTick, 1000);
        }
        setSequenceTimer(timerObj);
      }
      if (src.value === ELockInfo.ok || src.value === ELockInfo.err) {
        //turn off timer when sequence done
        const activeSequenceIds = activePopups
          .filter((p) => p?.obj?.id === src?.obj?.id)
          .map((p) => p?.obj?.id);
        if (activeSequenceIds.includes(src?.obj?.id)) {
          setSequenceTimer({ running: false, value: 0 });
        }
      }
      if (
        ![ELockInfo.ok, ELockInfo.err].includes(src.value) ||
        ([ELockInfo.ok, ELockInfo.err].includes(src.value) && answerIndicatorOn)
      ) {
        setActivePopups((activePopups) => [
          ...activePopups.filter((p) => p?.obj?.id !== currentItem?.obj?.id),
          currentItem,
        ]);
      } else {
        setActivePopups((activePopups) => [
          ...activePopups.filter((p) => p?.obj?.id !== currentItem?.obj?.id),
        ]);
      }
      setter(null);
    }
  }, [lockInfoPopupStatus, manualData, activePopups]);

  const timerTick = () => {
    setSequenceTimer((sequenceTimer) => {
      const timerObj = { ...sequenceTimer };
      timerObj.value -= 1000;
      if (timerObj.value > 0) {
        setTimeout(timerTick, 1000);
        return timerObj;
      } else {
        return { running: false, value: 0 };
      }
    });
  };

  const close =
    (popupId: number, clearTimer: boolean = false) =>
    () => {
      //check on list, remove from there
      if (clearTimer) setSequenceTimer({ value: 0, running: false });
      setActivePopups((activePopups) => activePopups.filter((p) => p.id !== popupId));
    };

  const getTextByType = (type: ELockInfo, obj: GameLock) => {
    switch (type) {
      case ELockInfo.ok:
        return translation.llabel_ok || 'Correct answer. Lock solved!';
      case ELockInfo.info_collect:
        return translation.llabel_collect || 'Collect $name $cur/$max';
      case ELockInfo.info_sequence:
        return translation.llabel_sequence || 'Sequence $name $cur/$max';
      default:
        return (
          (obj.multipleAttempts
            ? (
                translation.llabel_err_attempts || 'Incorrect answer. $attempts attempts left'
              ).replace(
                '$attempts',
                String(obj.quantityOfAttempts - (gameLockData[obj.id]?.attempts || 0)),
              )
            : translation.llabel_err || 'Incorrect answer.') +
          (obj?.type === LockType.Sequence ? ` - ${obj?.name}` : '')
        );
    }
  };

  const getContent = (activePopup: TLockInfoPopup) => {
    let text = (activePopup?.text || getTextByType(activePopup?.value, activePopup?.obj))
      .replace('$attempts', (gameLockData[activePopup?.obj?.id]?.attempts || '').toString())
      .replace('$name', (activePopup?.obj?.name || '').toString());
    if (
      activePopup.value === ELockInfo.info_collect ||
      activePopup.value === ELockInfo.info_sequence
    ) {
      text = text.replace('$cur', (activePopup?.obj?.collected?.length || '').toString());
      text = text.replace(
        '$max',
        (activePopup?.obj?.solution?.split(LOCK_SOLUTION_SEPARATOR)?.length || '').toString(),
      );
    }
    // .replace('$cur', (activePopup?.obj?.cur || '').toString())
    // .replace('$max', (activePopup?.obj?.max || '').toString())
    switch (activePopup?.value) {
      case ELockInfo.ok:
        return (
          <>
            <i className="fas fa-check rndd" />
            {text}
            <i className="fas fa-times lip-clss" onClick={close(activePopup.id)} />
          </>
        );
      case ELockInfo.info_collect:
      case ELockInfo.info_sequence:
        return (
          <>
            {text}
            {activePopup.obj.hint?.hint1_active ||
            activePopup.obj.hint?.hint2_active ||
            activePopup.obj.hint?.solution_active ? (
              <i className="rndd qstn" onClick={() => setShowNewHintModal(activePopup.obj.id)}>
                ?
              </i>
            ) : null}
            {activePopup.value === ELockInfo.info_sequence &&
              sequenceTimer.running &&
              sequenceTimer.value > 0 && <span>{sequenceTimer.value / 1000}s</span>}
            {activePopup?.obj?.guidCanBeClosed && (
              <i
                className="fas fa-times lip-clss"
                onClick={close(activePopup.id, activePopup.value === ELockInfo.info_sequence)}
              />
            )}
          </>
        );
      default:
        return (
          <>
            <i className="fas fa-times rndd" />
            {text}
            <i className="fas fa-times lip-clss" onClick={close(activePopup.id)} />
          </>
        );
    }
  };
  return (
    <MainWrap rtl={rtl}>
      <TempBlock>
        {activePopups
          .filter((p) => p.value !== ELockInfo.info_sequence && p.value !== ELockInfo.info_collect)
          .map((p) => (
            <PopupItem key={p.id} value={p?.value}>
              {getContent(p)}
            </PopupItem>
          ))}
      </TempBlock>
      <div className="lip-simpleFlex">
        <CollectBlock>
          {activePopups
            .filter((p) => p.value === ELockInfo.info_collect)
            .map((p) => (
              <PopupItem key={p.id} value={p?.value}>
                {getContent(p)}
              </PopupItem>
            ))}
        </CollectBlock>
        <SequenceBlock>
          {activePopups
            .filter((p) => p.value === ELockInfo.info_sequence)
            .map((p) => (
              <PopupItem key={p.id} value={p?.value}>
                {getContent(p)}
              </PopupItem>
            ))}
        </SequenceBlock>
      </div>
    </MainWrap>
  );
};

export default memo(LockInfoPopup);
